import styled, { css } from 'styled-components'
import { NavLink } from 'react-router-dom'
import { GTR, FONT, COLOR } from '@farewill/ui/tokens'

const StyledList = styled.ul<{ $secondary: boolean; $hasBorder: boolean }>`
  margin: 0;
  display: flex;
  ${({ $hasBorder }) =>
    $hasBorder && `border-bottom: 1px solid ${COLOR.GREY.LIGHT};`}

  ${({ $secondary }) => $secondary && `margin-top: ${GTR.M};`}
`

/**
 * [1] Evenly redistribute the small amount of whitespace around inactive tab
 *     labels caused by the sentinel copy of the label.
 */
const StyledListItem = styled.li`
  display: grid;
  text-align: center; /* [1] */

  &:not(:last-of-type) {
    margin-right: ${GTR.L};
  }
`

/**
 * This is a hidden span with font-weight equal to that used when the tab is
 * active. It's visually hidden and vertically stacked on top of the regular tab
 * label, and ensures that the width of the tab does not change between its
 * active and inactive states. This prevents subsequent tab labels from jumping
 * around when the active tab changes.
 */
const StyledSentinel = styled.span<{ $secondary: boolean }>`
  font-weight: ${FONT.WEIGHT.BOLD};
  font-size: ${({ $secondary }) => ($secondary ? FONT.SIZE.S : FONT.SIZE.M)};
  grid-area: 1 / 1;
  visibility: hidden;
`

const StyledNavLink = styled(NavLink)<{
  $secondary: boolean
  $paddingBottom: string
  $fontWeight?: string
  $borderColor: string
  $boldActiveLink: boolean
  $arrow: boolean
}>`
  display: block;
  padding-bottom: ${({ $paddingBottom }) => $paddingBottom};
  position: relative;
  text-decoration: none;
  color: ${COLOR.BLACK};
  font-weight: ${({ $secondary, $fontWeight }) =>
    $secondary ? FONT.WEIGHT.REGULAR : $fontWeight};
  font-size: ${({ $secondary }) => ($secondary ? FONT.SIZE.S : FONT.SIZE.M)};
  grid-area: 1 / 1;

  &:hover,
  &:focus {
    outline: 0;
    text-decoration: none;
  }

  &:after {
    content: '';
    display: block;
    height: 4px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  &:hover {
    &:after {
      background-color: color-mix(in srgb, ${COLOR.GREY.DARK} 15%, transparent);
    }
  }

  &:focus {
    &:after {
      background-color: color-mix(in srgb, ${COLOR.GREY.DARK} 35%, transparent);
    }
  }

  &.active {
    position: relative;
    ${({ $boldActiveLink }) =>
      $boldActiveLink && `font-weight: ${FONT.WEIGHT.BOLD};`}

    &:after {
      background-color: ${({ $borderColor }) => $borderColor};
    }

    ${({ $arrow, $borderColor }) =>
      $arrow &&
      css`
        &:before {
          content: '';
          width: 0;
          height: 0;
          border-left: 9px solid transparent;
          border-right: 9px solid transparent;
          border-top: 9px solid ${$borderColor};
          position: absolute;
          bottom: -9px;
          left: 50%;
          transform: translateX(-50%);
        }
      `}
  }
`

type NavigationTabsProps = {
  className?: string
  links: {
    label: string
    url: string
    exact?: boolean
    isActive?: () => boolean
  }[]
  isSecondary?: boolean
  hasSecondary?: boolean
  paddingBottom?: string
  hasBorder?: boolean
  borderColor?: string
  fontWeight?: number
  boldActiveLink?: boolean
}

const NavigationTabs = ({
  className,
  links = [],
  isSecondary = false,
  hasSecondary = false,
  paddingBottom = GTR.S,
  hasBorder = true,
  borderColor = COLOR.ACCENT.PRIMARY,
  boldActiveLink = true,
}: NavigationTabsProps) => (
  <nav>
    <StyledList
      $secondary={isSecondary}
      className={className}
      $hasBorder={hasBorder}
    >
      {links.map(({ label, url, exact = true, isActive }) => (
        <StyledListItem key={url}>
          <StyledSentinel aria-hidden $secondary={isSecondary}>
            {label}
          </StyledSentinel>
          <StyledNavLink
            exact={exact}
            to={url}
            activeClassName="active"
            $arrow={hasSecondary}
            $secondary={isSecondary}
            $paddingBottom={paddingBottom}
            $borderColor={borderColor}
            $boldActiveLink={boldActiveLink}
            isActive={isActive}
          >
            {label}
          </StyledNavLink>
        </StyledListItem>
      ))}
    </StyledList>
  </nav>
)

export default NavigationTabs
